import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import { api } from "@/lib/api";
import { addToCart, formatGBP, formatDate } from "@/lib/utils-cart";
import { Truck, ShieldCheck, RotateCcw, Star, ShoppingBag, Plus, Minus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [reviewing, setReviewing] = useState(false);
  const [review, setReview] = useState({ rating: 5, title: "", body: "" });

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => setProduct(r.data)).catch(() => toast.error("Product not found"));
  }, [id]);

  const onAdd = () => {
    if (!product) return;
    addToCart(product, qty);
    toast.success(`${product.title} added to cart`);
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) return toast.error("Sign in to review");
    try {
      await api.post("/reviews", { product_id: id, ...review });
      toast.success("Review posted");
      setReviewing(false);
      const fresh = await api.get(`/products/${id}`);
      setProduct(fresh.data);
    } catch (e) {
      toast.error("Failed to post review");
    }
  };

  if (!product) return <PublicLayout><div className="max-w-6xl mx-auto p-10 text-muted-foreground">Loading…</div></PublicLayout>;

  return (
    <PublicLayout>
      <section className="max-w-7xl mx-auto px-6 py-10 grid lg:grid-cols-2 gap-12">
        <div>
          <div className="aspect-square bg-muted overflow-hidden">
            <img src={product.images?.[0]} alt={product.title} className="w-full h-full object-cover" />
          </div>
        </div>
        <div>
          <div className="label-eyebrow text-secondary">{product.category}</div>
          <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight text-primary mt-2">{product.title}</h1>
          <div className="mt-2 text-sm text-muted-foreground">Sold by <span className="text-primary font-medium">{product.supplier_name}</span></div>

          {product.review_count > 0 && (
            <div className="mt-3 flex items-center gap-2 text-sm">
              <span className="flex text-amber-500">{[1,2,3,4,5].map(i => <Star key={i} size={14} fill={i <= Math.round(product.avg_rating) ? "currentColor" : "none"} />)}</span>
              <span className="text-muted-foreground">{product.avg_rating} ({product.review_count} reviews)</span>
            </div>
          )}

          <div className="mt-6 flex items-baseline gap-3">
            <div className="font-display font-black text-4xl text-foreground">{formatGBP(product.price)}</div>
            {product.compare_at_price > product.price && <div className="line-through text-muted-foreground">{formatGBP(product.compare_at_price)}</div>}
            <div className="text-xs uppercase font-mono tracking-wider text-accent-green">in stock</div>
          </div>

          <p className="mt-6 text-muted-foreground leading-relaxed">{product.description}</p>

          <div className="mt-8 flex items-center gap-3">
            <div className="flex items-center border border-border rounded-sm">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 py-2 hover:bg-muted" data-testid="qty-decrement"><Minus size={14} /></button>
              <span className="w-12 text-center font-mono" data-testid="qty-display">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="px-3 py-2 hover:bg-muted" data-testid="qty-increment"><Plus size={14} /></button>
            </div>
            <button onClick={onAdd} className="v-btn-primary flex-1" data-testid="add-to-cart">
              <ShoppingBag size={16} /> Add to cart · {formatGBP(product.price * qty)}
            </button>
          </div>

          <div className="mt-8 grid grid-cols-3 gap-2">
            <Trust icon={Truck} label="Free UK delivery from £50" />
            <Trust icon={ShieldCheck} label="Centralised payments" />
            <Trust icon={RotateCcw} label="30-day returns" />
          </div>
        </div>
      </section>

      <section className="border-t border-border bg-card">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-2xl font-bold text-primary">Reviews</h2>
            <button onClick={() => setReviewing(!reviewing)} className="v-btn-outline" data-testid="open-review-form">Write a review</button>
          </div>

          {reviewing && (
            <form onSubmit={submitReview} className="mt-6 v-card max-w-2xl">
              <div className="flex items-center gap-2">
                {[1,2,3,4,5].map(i => (
                  <button key={i} type="button" onClick={() => setReview({...review, rating: i})} data-testid={`rate-${i}`}>
                    <Star size={20} className="text-amber-500" fill={i <= review.rating ? "currentColor" : "none"} />
                  </button>
                ))}
              </div>
              <input className="v-input mt-4" placeholder="Title (optional)" value={review.title} onChange={(e) => setReview({...review, title: e.target.value})} data-testid="review-title" />
              <textarea className="v-input mt-3" rows={4} placeholder="What did you think?" value={review.body} onChange={(e) => setReview({...review, body: e.target.value})} required data-testid="review-body" />
              <div className="mt-4 flex gap-2">
                <button className="v-btn-primary" type="submit" data-testid="submit-review">Post review</button>
                <button type="button" className="v-btn-outline" onClick={() => setReviewing(false)}>Cancel</button>
              </div>
            </form>
          )}

          <div className="mt-8 grid md:grid-cols-2 gap-3">
            {product.reviews?.map((r) => (
              <div key={r.id} className="v-card">
                <div className="flex items-center justify-between">
                  <div className="font-medium text-primary">{r.user_name || "Vendora customer"}</div>
                  <span className="text-xs text-muted-foreground font-mono">{formatDate(r.created_at)}</span>
                </div>
                <div className="flex text-amber-500 mt-1">{[1,2,3,4,5].map(i => <Star key={i} size={12} fill={i <= r.rating ? "currentColor" : "none"} />)}</div>
                {r.title && <div className="mt-2 font-semibold text-foreground">{r.title}</div>}
                <p className="mt-1 text-sm text-muted-foreground">{r.body}</p>
              </div>
            ))}
            {(!product.reviews || product.reviews.length === 0) && (
              <div className="text-muted-foreground text-sm">Be the first to review this product.</div>
            )}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}

function Trust({ icon: Icon, label }) {
  return (
    <div className="border border-border p-3 rounded-sm">
      <Icon size={16} className="text-primary" />
      <div className="mt-2 text-xs font-medium text-foreground">{label}</div>
    </div>
  );
}
